import classNames from "classnames";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import styles from "./ComponentAnatomyView.module.scss";
import {
    FIGMA_SAMPLE_BULLETS,
    FIGMA_SAMPLE_CONTENT,
    FIGMA_SAMPLE_TITLE,
    FIGMA_SHORTCUT_LABEL,
} from "./TooltipPlaygroundMatrix";

// Figma's fixed tooltip width; the real cap needs the positioner's
// --available-width, which the plate does not have.
const POPUP_MAX_WIDTH_PX = 300;

interface TooltipSlots {
    hasTitle: boolean;
    hasContent: boolean;
    hasBullets: boolean;
    hasShortcut: boolean;
}

const textSlots: TooltipSlots = {
    hasTitle: false,
    hasContent: true,
    hasBullets: false,
    hasShortcut: false,
};

/** Which slots each Figma variant draws. */
const slotsByVariant: Partial<Record<string, TooltipSlots>> = {
    text: textSlots,
    title: { ...textSlots, hasTitle: true },
    titleBullets: { ...textSlots, hasTitle: true, hasBullets: true },
    bullets: { ...textSlots, hasBullets: true },
    shortcut: { ...textSlots, hasShortcut: true },
    shortcutOnly: { ...textSlots, hasContent: false, hasShortcut: true },
};

/** X-ray of the Tooltip popup in the chosen Figma variant, with Figma's sample text. */
export const TooltipSchematic = ({
    bindings,
    highlight,
    variantId,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    variantId: string;
}) => {
    const read = bindingReader(bindings);
    const slots = slotsByVariant[variantId] ?? textSlots;
    const isRow = slots.hasShortcut;
    const textStyle = (partId: "title" | "content") => ({
        color: read(partId, "color"),
        fontFamily: read(partId, "font-family"),
        fontSize: read(partId, "font-size"),
        fontWeight: read(partId, "font-weight"),
        lineHeight: read(partId, "line-height"),
        letterSpacing: read(partId, "letter-spacing"),
    });

    return (
        <span
            className={classNames(styles.schematic, styles.tooltipSchematic)}
            style={{ maxWidth: POPUP_MAX_WIDTH_PX }}
        >
            <Part
                partId="popup"
                highlight={highlight}
                className={styles.buttonBorder}
                style={{
                    background: read("popup", "background"),
                    borderColor: read("popup", "border-color"),
                    borderWidth: read("popup", "border-width"),
                    borderRadius: read("popup", "border-radius"),
                    boxShadow: read("popup", "box-shadow"),
                }}
            />
            <span
                className={styles.buttonContent}
                style={{
                    flexDirection: isRow ? "row" : "column",
                    alignItems: isRow ? "center" : "flex-start",
                    gap: read("popup", "gap"),
                    paddingBlock: read("popup", "padding-block"),
                    paddingInline: read("popup", "padding-inline"),
                    paddingInlineEnd: read("popup", "padding-inline-end"),
                }}
            >
                {slots.hasTitle && (
                    <Part
                        partId="title"
                        highlight={highlight}
                        style={textStyle("title")}
                    >
                        {FIGMA_SAMPLE_TITLE}
                    </Part>
                )}
                {slots.hasContent && (
                    <Part
                        partId="content"
                        highlight={highlight}
                        style={{
                            ...textStyle("content"),
                            whiteSpace: isRow ? "nowrap" : undefined,
                        }}
                    >
                        {isRow ? FIGMA_SHORTCUT_LABEL : FIGMA_SAMPLE_CONTENT}
                    </Part>
                )}
                {slots.hasBullets && (
                    <Part
                        partId="bullets"
                        highlight={highlight}
                        style={textStyle("content")}
                    >
                        {FIGMA_SAMPLE_BULLETS.map((bullet) => (
                            <span
                                key={bullet}
                                style={{
                                    display: "block",
                                    position: "relative",
                                    paddingInlineStart: read(
                                        "bullets",
                                        "padding-inline-start (li)",
                                    ),
                                }}
                            >
                                <span
                                    style={{
                                        position: "absolute",
                                        insetInlineStart: 0,
                                    }}
                                >
                                    •
                                </span>
                                {bullet}
                            </span>
                        ))}
                    </Part>
                )}
                {slots.hasShortcut && (
                    <Part partId="shortcut" highlight={highlight}>
                        <Shortcut keys="mod+a" />
                    </Part>
                )}
            </span>
        </span>
    );
};
